import {Booking} from '@/store/picklistPickerV2';
import {autoImplement} from '@/util';
import {ApiArticleEAN} from './ArticleEAN';
import {ApiArticleGebinde} from './ArticleGebinde';
import {ApiArticlePalette} from './ArticlePalette';
import {ApiBooking} from './Booking';
import {ApiBookingWarehouse} from './BookingWarehouse';
import {ApiCharge} from './Charge';
import {ApiInventory} from './Inventory';
import {ApiInventoryPositionReduced} from './InventoryPosition';
import NVE, {ApiNVE} from './NVE';
import Picklist from './Picklist';
import {ApiSuggestion} from './Suggestion';
import {ApiSuggestionPosition} from './SuggestionPosition';
import {ApiUmbuchungPosition} from '@/model/UmbuchungPosition';
import {ApiUmbuchung} from '@/model/Umbuchung';
import {ApiCorrection, ApiCorrectionPosition} from '@/model/Correction';

export interface ApiPosition {
  Posnr: number;
  Artikelnr: string;
  ArtikelArt: string;
  Bezeichn1: string;
  Bezeichn2: string;

  Menge: number;
  Einheit: string;
  Eancode: string;

  Gebindemenge: number | null;
  /**
   * Einheit des Gebindes
   */
  Gebindeinh: string | null;
  /**
   * Inhalt pro Gebinde
   */
  Gebinhalt: number | null;
  GebindeSchluessel: number;
  // eslint-disable-next-line camelcase
  EAN_Gebinde: string | null;

  Palettenmenge: number | null;
  Palettennr: number | null;
  Umrpalgebinde: number | null;
  Umrpalmenge: number | null;

  NVE?: string | null;

  Chargenpflicht: 'N' | 'J';
  Seriennrpflicht: 'N' | 'J';

  Matchcode: string;
  GruppenBezeichnung: string;

  // Auftrag
  Belegnr: number;
  Datum: string;
  Name: string;

  Buchungen: ApiBooking[];
  VerfuegbareNVEs?: ApiNVE[];
  Chargen: ApiCharge[];
  Restmenge: number;

  Eingehendeslager?: ApiBookingWarehouse | null;
  AbgehendesLager?: ApiBookingWarehouse | null;
  Bediener: string;

  ArtikelEAN?: ApiArticleEAN[];
  ArtikelGebinde?: ApiArticleGebinde[];
  ArtikelPaletten?: ApiArticlePalette[];

  // incompl
  Nabi?: boolean;
}

interface PositionInterface extends Omit<ApiPosition, 'VerfuegbareNVEs'> {
  ChargenpflichtBool: boolean;
  SeriennrpflichtBool: boolean;
  givenByApi: boolean;
  VerfuegbareNVEsCache: Partial<Record<string, NVE>>;
}

export default class Position extends autoImplement<PositionInterface>() {
  constructor(data: PositionInterface) {
    super(data);
    if (typeof this.givenByApi !== 'boolean') this.givenByApi = false;
  }

  static fromApi({VerfuegbareNVEs, ...apiPosition}: ApiPosition, givenByApi = true): Position {
    const Eancode = apiPosition.Eancode ? apiPosition.Eancode : apiPosition.Artikelnr;
    const verfuegbareNVEsEntries = VerfuegbareNVEs?.map((nve) => [nve.NVE, NVE.fromApi(nve)] satisfies [string, NVE]) ?? [];
    return new Position({
      ...apiPosition,
      VerfuegbareNVEsCache: Object.fromEntries(verfuegbareNVEsEntries),
      ChargenpflichtBool: apiPosition.Chargenpflicht === 'J',
      SeriennrpflichtBool: apiPosition.Seriennrpflicht === 'J',
      Eancode,
      EAN_Gebinde: apiPosition.EAN_Gebinde ?? `g-${Eancode}`,
      givenByApi,
    });
  }

  get VerfuegbareNVEs(): NVE[] {
    return Object.values(this.VerfuegbareNVEsCache as Record<string, NVE>);
  }

  get NameBelegnr() {
    return `${this.Name}-${this.Belegnr}`;
  }

  get uniqueIndex() {
    return `${this.Belegnr}-${this.Posnr}`;
  }

  get mengeProPalette() {
    return this.Gebinhalt && this.Umrpalgebinde ? this.Umrpalgebinde * this.Gebinhalt : null;
  }

  toReducedInventoryPosition(inventory: ApiInventory | Picklist, bookingData?: Booking): ApiInventoryPositionReduced {
    const Datum = (inventory as ApiInventory).Datum ?? (inventory as Picklist).Kmsdatum;
    return {
      Gebinde: {
        GebindeMenge: bookingData?.qtyGebinde ?? this.Buchungen[0]?.Gebindemenge ?? 0,
        GebindeEinheit: this.Gebindeinh,
        GebindeSchluessel: this.GebindeSchluessel,
        GebindeInhalt: this.Gebinhalt ?? 0,
      },
      PosNr: this.Posnr,
      Niederlassung: bookingData?.subsidiary ?? this.Buchungen[0]?.AbgehendesLager.Niederlassung ?? 0,
      Lagerhalle: bookingData?.warehouse ?? this.Buchungen[0]?.AbgehendesLager.Lagerhalle ?? 0,
      Lagerfach: bookingData?.shelf ?? this.Buchungen[0]?.AbgehendesLager.Lagerfach ?? 0,
      ArtikelNr: this.Artikelnr,
      ArtikelArt: this.ArtikelArt,
      ArtikelBezeichnung1: this.Bezeichn1,
      ArtikelBezeichnung2: this.Bezeichn2,
      Menge: bookingData?.qty ?? this.Buchungen[0]?.Menge ?? 0,
      Einheit: this.Einheit,
      ChargenNr: bookingData?.batchNo ?? this.Buchungen[0]?.AbgehendesLager.Chargennr ?? 0,
      NVE: bookingData?.nve ?? this.Buchungen[0]?.NVE ?? '',
      SerienNr: bookingData?.serialNo ? String(bookingData.serialNo) : '',
      Datum,
    };
  }

  toSuggestionPosition(suggestion: ApiSuggestion | Picklist, bookingData?: Booking): ApiSuggestionPosition {
    const booking: ApiBooking | undefined = this.Buchungen[0];
    return {
      PosNr: this.Posnr,
      Niederlassung: bookingData?.subsidiary ?? booking?.AbgehendesLager.Niederlassung ?? 0,
      Lagerhalle: bookingData?.warehouse ?? booking?.AbgehendesLager.Lagerhalle ?? 0,
      Lagerfach: bookingData?.shelf ?? booking?.AbgehendesLager.Lagerfach ?? 0,
      ArtikelNr: this.Artikelnr,
      ArtikelArt: this.ArtikelArt,
      ArtikelBezeichnung1: this.Bezeichn1,
      ArtikelBezeichnung2: this.Bezeichn2,
      GruppeBezeichnung: this.GruppenBezeichnung,
      ArtikelGruppe: this.Matchcode,
      Menge: bookingData?.qty ?? booking?.Menge ?? 0,
      GebindeMenge: bookingData?.qtyGebinde ?? booking?.Gebindemenge ?? 0,
      EANCode: this.Eancode,
      BuchungNr: (suggestion as ApiSuggestion).BuchungNr ?? (suggestion as Picklist).Picklistnr,
    };
  }

  toUmbuchungPosition(umbuchung: ApiUmbuchung | Picklist, bookingData?: Booking): ApiUmbuchungPosition {
    const booking: ApiBooking | undefined = this.Buchungen[0];
    return {
      Bediener: this.Bediener,
      VerfuegbareChargen: this.Chargen,
      Chargenpflicht: this.Chargenpflicht == 'J',
      Datum: '',
      Einheit: '',
      Erledigt: false,
      NVE: bookingData?.nve!,
      SerienNrpflicht: this.Seriennrpflicht == 'J',
      Uhrzeit: '',
      VerfuegbareNVEs: [],
      PosNr: this.Posnr,
      AbgehendesLager: {
        Niederlassung: bookingData!.subsidiary!,
        Lagerhalle: bookingData!.warehouse!,
        Lagerfach: bookingData?.shelf as number | undefined,
        Chargennr: bookingData?.batchNo as number | undefined,
      },
      EingehendesLager: {
        Niederlassung: bookingData!.destinationSubsidiary!,
        Lagerhalle: bookingData!.destinationWarehouse!,
        Lagerfach: bookingData?.destinationShelf as number | undefined,
        Chargennr: bookingData?.batchNo as number | undefined,
      },
      ArtikelNr: this.Artikelnr,
      ArtikelBezeichnung1: this.Bezeichn1,
      ArtikelBezeichnung2: this.Bezeichn2,
      GruppeBezeichnung: this.GruppenBezeichnung,
      ArtikelGruppe: this.Matchcode,
      Menge: bookingData?.qty ?? booking?.Menge ?? 0,
      EanCode: this.Eancode,
      BelegNr: (umbuchung as ApiUmbuchung).BelegNr ?? (umbuchung as Picklist).Picklistnr as number,
      Gebinde: {
        GebindeEinheit: this.Gebindeinh,
        GebindeSchluessel: this.GebindeSchluessel,
        GebindeInhalt: this.Gebinhalt,
        EAN_Gebinde: this.EAN_Gebinde,
        GebindeMenge: this.Gebindemenge,
      },
      Nabi: this.Nabi,
    };
  }

  toCorrectionPosition(correction: ApiCorrection | Picklist, bookingData?: Booking): ApiCorrectionPosition {
    const booking: ApiBooking | undefined = this.Buchungen[0];
    console.log('Booking: ', booking);
    return {
      VerfuegbareChargen: this.Chargen,
      Chargenpflicht: this.Chargenpflicht == 'J',
      Datum: '',
      Erledigt: false,
      NVE: bookingData?.nve!,
      SerienNrpflicht: this.Seriennrpflicht == 'J',
      Uhrzeit: '',
      VerfuegbareNVEs: [],
      PosNr: this.Posnr,
      ChargenNr: bookingData?.batchNo as number | undefined,
      Niederlassung: bookingData!.subsidiary!,
      Lagerhalle: bookingData!.warehouse!,
      Lagerfach: bookingData?.shelf as number | undefined,
      ArtikelNr: this.Artikelnr,
      ArtikelBezeichnung1: this.Bezeichn1,
      ArtikelBezeichnung2: this.Bezeichn2,
      GruppeBezeichnung: this.GruppenBezeichnung,
      ArtikelGruppe: this.Matchcode,
      Bediener: this.Bediener,
      IstMenge: {
        TatsaechlMenge: bookingData?.qty ?? booking?.Menge ?? 0,
        Einheit: this.Einheit,
      },
      EanCode: this.Eancode,
      BelegNr: (correction as ApiCorrection).BelegNr ?? (correction as Picklist).Picklistnr as number,
      Gebinde: {
        GebindeEinheit: this.Gebindeinh!,
        GebindeSchluessel: this.GebindeSchluessel,
        GebindeInhalt: this.Gebinhalt,
        EAN_Gebinde: this.EAN_Gebinde,
        GebindeMenge: this.Gebindemenge!,
      },
      Nabi: this.Nabi ? 'J' : 'N',
    };
  }
}
